import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { ONE_LINK_BASE_URL } from "../../constants";
import useEventBus from "../../hooks/useEventBus";
import LinkButton from "../buttons/button/LinkButton";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import useAppDownloadNudgeEnabled from "./useAppDownloadNudgeEnabled";
import styles from "./AppDownloadNudge.scss";

const DOWNLOAD_APP_URL_PATH = "/9d49isl5";
const QR_CODE_URL_PATH = "/2mv2lyo7";

type RuntimeConstants = {
  getTranslation: (key: string) => string;
  isMobile: boolean;
};

type NotificationBarEventData = {
  type: string;
  isBackInStockOptIn?: boolean;
};

const AppDownloadQrCode = (): JSX.Element | null => {
  const { getTranslation } = useRuntimeConstants() as RuntimeConstants;
  const generatedUrl = `${ONE_LINK_BASE_URL}${QR_CODE_URL_PATH}`;
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);

  useEffect(() => {
    QRCode.toDataURL(generatedUrl, {
      margin: 1,
      width: 100,
    }).then((url: string) => setQrCodeDataUrl(url));
  }, [generatedUrl]);

  if (!qrCodeDataUrl) return null;

  const appPromptTitle = getTranslation(
    "back_in_stock_download_the_app_prompt_qr_code_title",
  );

  return (
    <div className={styles.container}>
      <p className={styles.intro}>
        {getTranslation("back_in_stock_download_the_app_prompt_body")}
      </p>
      <div className={styles.qrRow}>
        <img
          src={qrCodeDataUrl}
          alt={appPromptTitle}
          className={styles.qrCode}
        />
        <p className={styles.qrLabel}>{appPromptTitle}</p>
      </div>
    </div>
  );
};

const AppDownloadButton = (): JSX.Element => {
  const { getTranslation } = useRuntimeConstants() as RuntimeConstants;
  const buttonText = getTranslation("download_the_app_cta_title");
  const href = `${ONE_LINK_BASE_URL}${DOWNLOAD_APP_URL_PATH}`;

  return (
    <div className={styles.downloadContainer}>
      <p>{getTranslation("back_in_stock_download_the_app_prompt_body")}</p>
      <LinkButton
        href={href}
        ariaLabel={buttonText}
        type={BUTTON_TYPES.primary}
        additionalClasses={styles.downloadButton}
      >
        <span className={styles.text}>{buttonText}</span>
      </LinkButton>
    </div>
  );
};

const AppDownloadNudge = (): JSX.Element | null => {
  const { getTranslation, isMobile } =
    useRuntimeConstants() as RuntimeConstants;
  const [showNudge, setShowNudge] = useState(false);

  useEventBus(
    "notificationBar",
    ({ type, isBackInStockOptIn }: NotificationBarEventData) => {
      if (type === "success" && isBackInStockOptIn) {
        setShowNudge(true);
      }
    },
    [],
  );

  const isEnabled = useAppDownloadNudgeEnabled();

  if (!showNudge || !isEnabled) {
    return null;
  }

  return (
    <>
      <h2 className={styles.appDownloadSubheader}>
        {getTranslation("back_in_stock_download_the_app_prompt_title")}
      </h2>
      {isMobile ? <AppDownloadButton /> : <AppDownloadQrCode />}
    </>
  );
};

export default AppDownloadNudge;
